import React, { useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import usePermissions from '../../hooks/usePermissions';
import { BRAND } from '../../theme/brandTokens';

const PermissionProtectedRoute = ({
  children,
  permissions = [],
  ownPermissions = [],
  ownResourceIdParam,
  ownerAlsoAllowed = false,
  requireAll = false,
  storeId: storeIdProp,
  redirectTo = '/management/stores',
  accessCheck,
  loadingFallback,
}) => {
  const params = useParams();
  const { managementUser } = useAuth();
  const storeId = storeIdProp ?? params.storeId;
  const {
    hasAnyPermission,
    hasAllPermissions,
    isOwner,
    loading,
  } = usePermissions(storeId);

  const isAccessAllowed = useMemo(() => {
    if (typeof accessCheck === 'function') {
      return accessCheck({
        hasAnyPermission,
        hasAllPermissions,
        isOwner,
        params,
        managementUser,
        storeId,
      });
    }

    if (ownerAlsoAllowed && isOwner) {
      return true;
    }

    if (ownPermissions.length > 0 && ownResourceIdParam) {
      const resourceId = params[ownResourceIdParam];
      if (resourceId && String(managementUser?.id) === String(resourceId) && hasAnyPermission(ownPermissions)) {
        return true;
      }
    }

    if (permissions.length === 0) {
      return true;
    }

    return requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
  }, [
    accessCheck,
    hasAllPermissions,
    hasAnyPermission,
    isOwner,
    managementUser,
    ownerAlsoAllowed,
    ownPermissions,
    ownResourceIdParam,
    params,
    permissions,
    requireAll,
    storeId,
  ]);

  if (loading) {
    if (loadingFallback) {
      return loadingFallback;
    }

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: BRAND.accent }} />
      </Box>
    );
  }

  if (!isAccessAllowed) {
    const target = typeof redirectTo === 'function' ? redirectTo(storeId, params) : redirectTo;
    return <Navigate to={target} replace />;
  }

  return children;
};

export default PermissionProtectedRoute;
